import { MutableRefObject, useInsertionEffect, useRef } from "react";

/**
 * Returns a ref whose `.current` is always synchronized to the latest `value`.
 *
 * Use this when you need to read a reactive value inside a `useEffect` or `useCallback`
 * without including it in the dependency array.
 *
 * @example
 * ```ts
 * const stableOnLoaded = useStableRef(onLoaded);
 * useEffect(() => {
 *   // data is loaded when the component mounts
 *   // but is not reloaded if the onLoaded callback changes
 *   loadData().then(() => stableOnLoaded.current());
 * }, [stableOnLoaded]); // lint requires this dependency, but the ref will not change or trigger the effect
 * ```
 *
 * @param value - the reactive value to track
 * @returns a stable ref whose `.current` reflects the latest `value`
 */
export function useStableRef<T>(value: T): MutableRefObject<T> {
  const valueRef = useRef(value);

  /*
   * Insertion effect is used here to ensure the ref is updated before any subsequent layout-effects and effects.
   * With React 19 this can be replaced by useEffectEvent() as the canonical way to provide non-reactive values to effects.
   * See the implementation of useEffectEvent ponyfill for an example of using insertion-effect for this type of behavior:
   * https://github.com/sanity-io/use-effect-event/blob/main/src/useEffectEvent.ts
   */
  useInsertionEffect(() => {
    valueRef.current = value;
  }, [value]);

  return valueRef;
}
